import {
  BackfillRecipeDto,
  FillType,
  RheologyDataDto,
  ThroughputControlType
} from 'providers/api';
import { enqueueSnackbar } from 'notistack';
import { all, values } from 'ramda';
import { isNumber } from 'utils';
import { Model } from '../hooks/useModelState';
import { updateRecipeState } from '../canvas/Model/updateRecipeState';
import { calculateFrictionForRoute } from '../canvas/Model/updateFriction';
import {
  generateAllRoutes,
  isRouteActive,
  liveRouteFor,
  rebuildStoredRoute,
  returnShortestRoute,
  routeOptsForProject
} from '../MineModel/Route/utils';
import { describeRouteFailure, routeFailureKey } from '../MineModel/Route/errors';
import { resetConstraintNodes } from '../Scenes/scene-utils';
import {
  OptimisationErrorType,
  OptimisationTargetLineResult,
  OptimisationTargetType,
  PasteRecipe,
  RecipeState
} from '../MineModel/types';
import PCRoute from '../MineModel/Route/PCRoute';
import PCStope from '../MineModel/Stopes/PCStope';

// A recipe, pour or flush records its route as the NODE walk `spoolIds` (the legacy name) and, when saved
// since the SPA started sending them, the real spools `routeSpoolIds`. Rebuilt from those rather than searched
// for, a spool deactivated since the route was planned stays on it, and the result depends on nothing in `model.routes`.
export const findStoredRoute = (
  model: Model,
  spoolIds: string[],
  routeSpoolIds?: string[],
  stope: PCStope | undefined = model.selectedStope,
): PCRoute | undefined => {
  if (!stope) return undefined;
  try {
    return rebuildStoredRoute(spoolIds, routeSpoolIds, stope, model.spools, model.nodes, model.pipes);
  } catch {
    return undefined;
  }
};

export interface FlushRoutePick {
  route?: PCRoute;
  replanning: boolean;
}

// Without a stored route to keep, a flush plans on an all-active route; for an existing flush that is a re-plan.
export const pickFlushRoute = (
  storedRoute: PCRoute | undefined,
  selected: PCRoute | undefined,
  routes: Record<string, PCRoute>,
  isExistingFlush: boolean,
): FlushRoutePick => {
  const route = storedRoute
    ?? (selected && isRouteActive(selected) ? selected : undefined)
    ?? liveRouteFor(routes);
  return { route, replanning: isExistingFlush && !storedRoute && !!route };
};

// A redrawn stored route over an inactive spool stays in `model.routes`, so regenerate the stope's routes before a new plan.
export const resetRoutesIfStoredRouteShown = (model: Model): void => {
  if (model.selectedStope && !all(isRouteActive, values(model.routes ?? {}))) {
    model.setSelectedStope(model.selectedStope);
  }
};

const cloneRouteWithPressures = (sourceRoute: PCRoute): PCRoute => {
  const cloned = Object.create(Object.getPrototypeOf(sourceRoute));
  Object.assign(cloned, sourceRoute);
  cloned.routePoints = sourceRoute.routePoints.map((rp) => ({ ...rp }));
  return cloned;
};

interface ApplyRecipeVisualsParams {
  model: Model;
  recipe: BackfillRecipeDto;
  rheologyDataSet: RheologyDataDto;
  fillType: FillType;
}

export const applyRecipeModelVisuals = ({
  model,
  recipe,
  rheologyDataSet,
  fillType,
}: ApplyRecipeVisualsParams): void => {
  model.setOptimiserResults(undefined);

  const backfillPlantNodeIdFromRoute = recipe.spoolIds?.[recipe.spoolIds.length - 1];
  const selectedBackfillPlantNodeId = model.activeBackfillPlant?.id;

  if (backfillPlantNodeIdFromRoute && backfillPlantNodeIdFromRoute !== selectedBackfillPlantNodeId && model.nodes[backfillPlantNodeIdFromRoute] && model.selectedStope) {
    model.setActiveBackfillPlant(model.nodes[backfillPlantNodeIdFromRoute]);
    try {
      const { routeObjects } = generateAllRoutes(
        model.nodes[backfillPlantNodeIdFromRoute],
        model.spools,
        model.selectedStope!,
        Object.values(model.pipes),
        routeOptsForProject(model.project),
        model.nodes,
        model.pipes,
      );
      model.setRoutes(routeObjects);
    } catch (error) {
      model.setRoutes({});
      enqueueSnackbar(describeRouteFailure(error), { variant: 'error', key: routeFailureKey(error) });
    }
  }

  const route = recipe.spoolIds?.length ? findStoredRoute(model, recipe.spoolIds, recipe.routeSpoolIds) : undefined;
  if (route) {
    model.setSelectedRoute(route);
  } else if (backfillPlantNodeIdFromRoute && model.nodes[backfillPlantNodeIdFromRoute] && model.selectedStope) {
    resetConstraintNodes(model);
    try {
      // The stored route no longer exists, so the shortest route over active spools stands in for it.
      const { routeObjects } = generateAllRoutes(
        model.nodes[backfillPlantNodeIdFromRoute],
        model.spools,
        model.selectedStope!,
        Object.values(model.pipes),
        routeOptsForProject(model.project),
        model.nodes,
        model.pipes,
      );
      model.setSelectedRoute(returnShortestRoute(routeObjects));
    } catch (error) {
      model.setRoutes({});
      enqueueSnackbar(describeRouteFailure(error), { variant: 'error', key: routeFailureKey(error) });
    }
  }

  if (fillType === FillType.Paste && recipe.pasteSelectedSpecification) {
    const massConc = recipe.pasteSelectedSpecification.massConcentration;
    const binderConc = recipe.pasteSelectedSpecification.binderContent;

    if (isNumber(massConc) && isNumber(binderConc)) {
      const baseRecipeState: RecipeState = {
        ...(model.recipeState ?? {} as RecipeState),
        rheologyDataSetId: rheologyDataSet.rheologyDataSetId,
      };
      const recipeState = updateRecipeState(
        model,
        baseRecipeState,
        massConc / 100,
        binderConc / 100,
        rheologyDataSet?.tailingsDrySolidsDensity,
        rheologyDataSet?.binderDryParticleDensity,
        rheologyDataSet?.carrierFluidDensity,
        recipe.pasteSpecification?.throughputControlType === ThroughputControlType.DryTonnage ? recipe.pasteSpecification?.throughput : undefined,
        recipe.pasteSpecification?.throughputControlType === ThroughputControlType.FlowRate ? recipe.pasteSpecification?.throughput : undefined,
        recipe.pasteSpecification?.throughputControlType === ThroughputControlType.WetTonnage ? recipe.pasteSpecification?.throughput : undefined,
        recipe.pasteSelectedSpecification?.pumpPressure,
        recipe.pasteSelectedSpecification?.ucsStrength,
      );
      model.setRecipeState({
        ...recipeState,
        pipeRatingOffsetPercent: recipe.nominalParameters?.pipeRatingOffsetPercent ?? undefined,
      });

      const recalculatedRoute = route ? calculateFrictionForRoute(model, recipeState, route) : undefined;
      if (recalculatedRoute) {
        model.setSelectedRoute(recalculatedRoute);
      }

      // flowRateOverride is supplied only for flow rate target lines, which run the recipe's mix at
      // their own throughput. Without it — and without the matching control-type override, since
      // updateRecipeState otherwise reads model.project.throughput — the target reconstructs at the
      // recipe's throughput, which for a flow rate target (inherited Cw and binder) draws it exactly
      // on top of the optimised line. Mirrors the same helper in RecipeForm's reconstruction effect.
      const buildPasteRecipe = (
        spec: typeof recipe.pasteOptimisedSpecification,
        baseRoute: PCRoute,
        flowRateOverride?: number,
      ): PasteRecipe | undefined => {
        if (!spec) return undefined;

        const concentrationByMass = spec.massConcentration / 100;
        const cement = spec.binderContent / 100;

        const scenarioRecipeState = updateRecipeState(
          model,
          recipeState,
          concentrationByMass,
          cement,
          rheologyDataSet?.tailingsDrySolidsDensity,
          rheologyDataSet?.binderDryParticleDensity,
          rheologyDataSet?.carrierFluidDensity,
          undefined,
          flowRateOverride,
          undefined,
          undefined,
          undefined,
          flowRateOverride ? ThroughputControlType.FlowRate : undefined,
        );

        calculateFrictionForRoute(model, scenarioRecipeState, baseRoute);

        const clonedRoute = cloneRouteWithPressures(baseRoute);

        return {
          cement,
          concentrationByMass,
          actualUCSKpa: spec.ucsStrength,
          actualPumpPressureKpa: spec.pumpPressure * 100,
          error: OptimisationErrorType.NONE,
          route: clonedRoute,
          slurryDensity: scenarioRecipeState.wetDensity ?? 1000,
        };
      };

      const routeForReconstruction = recalculatedRoute ?? route;
      if (!routeForReconstruction) return;

      const optimisedResult: PasteRecipe | undefined = recipe.pasteSelectedSpecification
        ? {
          cement: recipe.pasteSelectedSpecification.binderContent / 100,
          concentrationByMass: recipe.pasteSelectedSpecification.massConcentration / 100,
          actualUCSKpa: recipe.pasteSelectedSpecification.ucsStrength,
          actualPumpPressureKpa: recipe.pasteSelectedSpecification.pumpPressure * 100,
          error: OptimisationErrorType.NONE,
          route: cloneRouteWithPressures(routeForReconstruction),
          slurryDensity: recipeState.wetDensity ?? 1000,
        }
        : undefined;

      const slackFlowResult = recipe.pasteSlackFlowSpecification
        ? buildPasteRecipe(recipe.pasteSlackFlowSpecification, routeForReconstruction)
        : undefined;
      const maxPressureResult = recipe.pasteMaxPressureSpecification
        ? buildPasteRecipe(recipe.pasteMaxPressureSpecification, routeForReconstruction)
        : undefined;

      const targetLineResults: OptimisationTargetLineResult[] | undefined = recipe.targetLines?.length
        ? recipe.targetLines.map((tl) => {
          const tlResult = tl.result
            ? buildPasteRecipe(
              tl.result,
              cloneRouteWithPressures(routeForReconstruction),
              tl.targetType === OptimisationTargetType.FlowRate ? (tl.flowRateTarget ?? undefined) : undefined,
            )
            : undefined;
          return {
            targetType: tl.targetType ?? OptimisationTargetType.KPaPerM,
            kPaPerMTarget: tl.kPaPerMTarget ?? undefined,
            targetPipeId: tl.targetPipeId ?? undefined,
            pumpPressureTarget: tl.pumpPressureTarget ?? undefined,
            yieldTarget: tl.yieldTarget ?? undefined,
            flowRateTarget: tl.flowRateTarget ?? undefined,
            result: tlResult,
            hasErrors: tl.hasErrors ?? false,
            errorDescription: tl.errorDescription ?? undefined,
          };
        })
        : undefined;

      calculateFrictionForRoute(model, recipeState, routeForReconstruction);

      if (optimisedResult) {
        model.setOptimiserResults({
          optimisedResult,
          slackFlowResult,
          maxPressureResult,
          targetLineResults,
        });
      }
    }
  } else if (fillType === FillType.Hydraulic && recipe.hydraulicSelectedSpecification) {
    const massConc = recipe.hydraulicSelectedSpecification.massConcentration;
    const binderRatio = recipe.hydraulicSpecification?.binderTailingsRatio ?? 0.02;

    if (massConc) {
      const baseRecipeState: RecipeState = {
        ...(model.recipeState ?? {} as RecipeState),
        rheologyDataSetId: rheologyDataSet.rheologyDataSetId,
      };
      const recipeState = updateRecipeState(
        model,
        baseRecipeState,
        massConc / 100,
        binderRatio,
        rheologyDataSet?.tailingsDrySolidsDensity,
        rheologyDataSet?.binderDryParticleDensity,
        rheologyDataSet?.carrierFluidDensity,
        recipe.hydraulicSpecification?.throughputControlType === ThroughputControlType.DryTonnage ? recipe.hydraulicSpecification?.throughput : undefined,
        recipe.hydraulicSpecification?.throughputControlType === ThroughputControlType.WetTonnage ? recipe.hydraulicSpecification?.throughput : undefined,
        recipe.hydraulicSpecification?.throughputControlType === ThroughputControlType.FlowRate ? recipe.hydraulicSpecification?.throughput : undefined,
      );
      model.setRecipeState(recipeState);

      const recalculatedRoute = calculateFrictionForRoute(model, recipeState);
      if (recalculatedRoute) {
        model.setSelectedRoute(recalculatedRoute);
      }
    }
  }
};
